import { DI } from '@intigral/constant/config/DIContainer';
import { STORE_NAME } from '@intigral/constant';
import { container, IGetChannelResponseListing } from '@intigral/domain';
import { IChannelService } from '@intigral/domain/lib/application/services/ChannelService';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { isFreeTier } from '@intigral/utils/lib/user';
import { appConfig } from '../config/app.config';
import channelsUtil from '../helpers/channelsUtil';
import { ChannelsAndGenres } from '../interfaces/StoreTypes';
import { pluck } from '../helpers/common';
import { checkShowLockIcon } from '../helpers/vod';
import moment from 'moment';

const isOutsideRewindDuration = (startTime: number, rewindBuffer: number, isLive = false) => {
    if (!startTime || isLive) return false;

    const now = Date.now();
    const bufferMs = rewindBuffer * 60 * 60 * 1000; // hours → ms
    const cutoffTime = now - bufferMs;

    return startTime < cutoffTime;
};

const mapListingToProgram = (
    listing: IGetChannelResponseListing,
    channelId: string,
    programChannel: ChannelsAndGenres['channels'][number] | undefined,
    isFreeUser: boolean
) => {
    const baseProgram = {
        id: listing.paId,
        description: listing.description,
        since: new Date(listing.startTime),
        till: new Date(listing.endTime),
        image: listing.images[0]?.imageUrl ?? '',
        channelUuid: channelId,
        channel: programChannel,
        listingId: listing.listingId,
        ...listing,
    };

    if (!isOutsideRewindDuration(listing.startTime, channelsUtil.getRewindBuffer(programChannel as any))) {
        return baseProgram;
    }

    if (isFreeUser) {
        return { ...baseProgram, locked: true };
    }

    return null;
};

const mapListingsToPrograms = (
    byChannelId: Record<string, IGetChannelResponseListing[]>,
    modifiedChannels: ReturnType<typeof buildModifiedChannels>,
    isFreeUser: boolean
) =>
    Object.entries(byChannelId).flatMap(([channelId, listings]) =>
        listings
            .map((listing) => {
                const programChannel = modifiedChannels?.find((channel) => channel?.channelID === channelId);
                return mapListingToProgram(listing, channelId, programChannel, isFreeUser);
            })
            .filter(Boolean)
    );

const buildModifiedChannels = (channels: ChannelsAndGenres, day: string) =>
    channels?.channels?.map((channel) => {
        const subscriptionGuids = pluck(channel?.subscriptions, 'subscriptionGUID');
        const isLocked = checkShowLockIcon(subscriptionGuids ?? []);
        const isLockedEpg = channelsUtil?.isLockedEpgChannel(channel, day) ?? false;

        return {
            ...channel,
            locked: isLocked,
            isLockedEpg,
        };
    });

export const fetchEpgDataByDay = async (day: string, rewindDay: string, channels: ChannelsAndGenres) => {
    const channelService = container.resolve<IChannelService>(DI.SERVICES.IChannelService);
    const timeZoneOffset = String(channelsUtil.getTimeZoneOffset());
    let days = [];
    const today = moment().format('YYYY-MM-DD');
    if (rewindDay && rewindDay < day) {
        // only set +1 if current date is today, because we are calling tommorrrow along with today
        if (day == today) {
            days = [rewindDay, day, addOneDay(day)];
        } else {
            days = [rewindDay, day];
        }
    } else {
        // only set +1 if current date is today, because we are calling tommorrrow along with today
        if (day == today) {
            days = [day, addOneDay(day)];
        } else {
            days = [day];
        }
    }
    const data = days.map(async (d) =>
        channelService.getChannelSchedule({
            timeZoneOffset: timeZoneOffset,
            productKey: appConfig.productKey,
            date: d,
            byId: '',
        })
    );

    const results = await Promise.all(data);
    const result = results.flatMap((r) => r);

    const byChannelId = result.reduce((acc: Record<string, IGetChannelResponseListing[]>, curr) => {
        acc[curr.channelId] = acc[curr.channelId] || [];
        acc[curr.channelId].push(...curr.listings);
        return acc;
    }, {});

    const modifiedChannels = buildModifiedChannels(channels, day);
    const isFreeUser = isFreeTier(get(STORE_NAME.USER));

    return mapListingsToPrograms(byChannelId, modifiedChannels, isFreeUser);
};

export const fetchEpgDataByChannelId = async (
    day: string,
    rewindDay: string,
    channels: ChannelsAndGenres,
    channelId: string
) => {
    const channelService = container.resolve<IChannelService>(DI.SERVICES.IChannelService);
    const timeZoneOffset = String(channelsUtil.getTimeZoneOffset());
    let days = [];
    const today = moment().format('YYYY-MM-DD');
    days = [day];

    const data = days.map(async (d) =>
        channelService.getChannelSchedule({
            timeZoneOffset: timeZoneOffset,
            productKey: appConfig.productKey,
            date: d,
            byId: channelId,
        })
    );

    const results = await Promise.all(data);
    const result = results.flatMap((r) => r);

    const byChannelId = result.reduce((acc: Record<string, IGetChannelResponseListing[]>, curr) => {
        acc[curr.channelId] = acc[curr.channelId] || [];
        acc[curr.channelId].push(...curr.listings);
        return acc;
    }, {});

    const modifiedChannels = buildModifiedChannels(channels, day);
    const isFreeUser = isFreeTier(get(STORE_NAME.USER));

    return mapListingsToPrograms(byChannelId, modifiedChannels, isFreeUser);
};

const addOneDay = (dateString) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
